import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign, TrendingUp, TrendingDown, Users, Car, Briefcase,
  Target, ChevronDown, Plus, Trash2, Loader2
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from "recharts";

const COLORS = ["#dc2626", "#18181b", "#6b7280", "#f59e0b", "#3b82f6", "#10b981", "#8b5cf6", "#ec4899"];

export default function FinancialDashboard() {
  const [summary, setSummary] = useState(null);
  const [byTech, setByTech] = useState([]);
  const [byVehicle, setByVehicle] = useState([]);
  const [byJob, setByJob] = useState([]);
  const [trend, setTrend] = useState([]);
  const [goals, setGoals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState("30");
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [goalForm, setGoalForm] = useState({
    location_id: "", period_type: "monthly", period_start: new Date().toISOString().split("T")[0],
    target_revenue: "", target_jobs: "", notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const days = parseInt(dateRange);
      const from = new Date(Date.now() - days * 86400000).toISOString();
      const to = new Date().toISOString();
      const params = `?date_from=${from}&date_to=${to}`;

      const [sumRes, techRes, vehRes, jobRes, trendRes, goalRes, locRes] = await Promise.all([
        API.get(`/financial/pnl/summary${params}`),
        API.get(`/financial/pnl/by-technician${params}`),
        API.get(`/financial/pnl/by-vehicle${params}`),
        API.get(`/financial/pnl/by-job${params}&limit=20`),
        API.get(`/financial/pnl/trend?days=${days}`),
        API.get("/financial/goals/progress"),
        API.get("/management/locations"),
      ]);
      setSummary(sumRes.data);
      setByTech(techRes.data.technicians);
      setByVehicle(vehRes.data.vehicles);
      setByJob(jobRes.data.jobs);
      setTrend(trendRes.data.trend);
      setGoals(goalRes.data.goals);
      setLocations(locRes.data.locations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createGoal = async () => {
    if (!goalForm.location_id) { toast.error("Select a location"); return; }
    try {
      await API.post("/financial/goals", {
        ...goalForm,
        target_revenue: parseFloat(goalForm.target_revenue) || 0,
        target_jobs: parseInt(goalForm.target_jobs) || 0,
      });
      toast.success("Goal created");
      setShowGoalForm(false);
      setGoalForm({ location_id: "", period_type: "monthly", period_start: new Date().toISOString().split("T")[0], target_revenue: "", target_jobs: "", notes: "" });
      fetchData();
    } catch (err) { toast.error("Failed to create goal"); }
  };

  const deleteGoal = async (id) => {
    try {
      await API.delete(`/financial/goals/${id}`);
      toast.success("Goal deleted");
      fetchData();
    } catch (err) { toast.error("Failed to delete"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "technicians", label: "By Technician" },
    { id: "vehicles", label: "By Vehicle" },
    { id: "jobs", label: "By Job" },
    { id: "goals", label: "Goals" },
  ];

  const pieData = summary?.expenses_by_category
    ? Object.entries(summary.expenses_by_category).map(([k, v]) => ({ name: k.replace("_", " "), value: v }))
    : [];

  return (
    <div className="space-y-6" data-testid="financial-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">P&L Dashboard</h1>
          <p className="text-sm text-zinc-500">Financial performance & reporting</p>
        </div>
        <select value={dateRange} onChange={e => setDateRange(e.target.value)}
          className="px-3 py-1.5 border border-zinc-300 rounded-lg text-sm bg-white"
          data-testid="date-range-select">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pnl-kpi-grid">
          <Card className="border border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">Revenue</span>
              </div>
              <p className="text-2xl font-heading font-bold text-emerald-700">${summary.total_revenue.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">Expenses</span>
              </div>
              <p className="text-2xl font-heading font-bold text-red-700">${summary.total_expenses.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-zinc-600" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">Net Profit</span>
              </div>
              <p className={`text-2xl font-heading font-bold ${summary.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                ${summary.net_profit.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="w-4 h-4 text-blue-600" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">Jobs</span>
              </div>
              <p className="text-2xl font-heading font-bold text-zinc-900">{summary.total_jobs}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === t.id ? "border-red-600 text-red-700" : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`} data-testid={`pnl-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border border-zinc-200" data-testid="revenue-trend-chart">
            <CardContent className="p-5">
              <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Revenue vs Expenses Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue" dot={false} />
                  <Line type="monotone" dataKey="expenses" stroke="#dc2626" strokeWidth={2} name="Expenses" dot={false} />
                  <Line type="monotone" dataKey="profit" stroke="#18181b" strokeWidth={2} name="Profit" strokeDasharray="5 5" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border border-zinc-200" data-testid="expense-breakdown-chart">
            <CardContent className="p-5">
              <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Expense Breakdown</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: "#a1a1aa", strokeWidth: 1 }}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-zinc-400 text-sm">No expense data</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* By Technician Tab */}
      {activeTab === "technicians" && (
        <div className="space-y-4">
          <Card className="border border-zinc-200" data-testid="tech-pnl-chart">
            <CardContent className="p-5">
              <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">P&L by Technician</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byTech} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                  <Bar dataKey="expenses" fill="#dc2626" name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tech-pnl-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Technician</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Revenue</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Expenses</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Profit</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Jobs</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Mileage</th>
                </tr>
              </thead>
              <tbody>
                {byTech.map(t => (
                  <tr key={t.vendor_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2.5 px-3 font-medium text-zinc-800">{t.name}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-700 font-medium">${t.revenue.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-red-600">${t.expenses.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right font-bold ${t.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      ${t.profit.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right text-zinc-600">{t.jobs_count}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-500">{t.mileage} mi</td>
                  </tr>
                ))}
                {byTech.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-zinc-400">No data for this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Vehicle Tab */}
      {activeTab === "vehicles" && (
        <div className="space-y-4">
          <Card className="border border-zinc-200" data-testid="vehicle-cost-chart">
            <CardContent className="p-5">
              <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Vehicle Costs</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byVehicle}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="gas_cost" fill="#f59e0b" name="Gas" stackId="a" />
                  <Bar dataKey="maintenance_cost" fill="#dc2626" name="Maintenance" stackId="a" />
                  <Bar dataKey="total_cost" fill="#18181b" name="Total" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="vehicle-cost-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Vehicle</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Assigned To</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Gas</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Maintenance</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Total Cost</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Mileage</th>
                  <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">$/Mile</th>
                </tr>
              </thead>
              <tbody>
                {byVehicle.map(v => (
                  <tr key={v.vehicle_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2.5 px-3">
                      <p className="font-medium text-zinc-800">{v.name}</p>
                      <p className="text-[10px] text-zinc-400">{v.make_model} {v.license_plate && `- ${v.license_plate}`}</p>
                    </td>
                    <td className="py-2.5 px-3 text-zinc-600">{v.assigned_to || "—"}</td>
                    <td className="py-2.5 px-3 text-right text-amber-600">${v.gas_cost.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-red-600">${v.maintenance_cost.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right font-bold text-zinc-900">${v.total_cost.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-500">{v.total_mileage} mi</td>
                    <td className="py-2.5 px-3 text-right text-zinc-500">${v.cost_per_mile}</td>
                  </tr>
                ))}
                {byVehicle.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-zinc-400">No vehicles with expenses</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Job Tab */}
      {activeTab === "jobs" && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="job-pnl-table">
            <thead>
              <tr className="border-b border-zinc-200 text-left">
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Job</th>
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Service</th>
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Technician</th>
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Revenue</th>
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Expenses</th>
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px] text-right">Profit</th>
                <th className="py-2 px-3 font-bold text-zinc-500 uppercase text-[10px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {byJob.map(j => (
                <tr key={j.job_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="py-2.5 px-3">
                    <span className="font-mono text-zinc-500">{j.request_code}</span>
                  </td>
                  <td className="py-2.5 px-3 text-zinc-700">{j.service_type}</td>
                  <td className="py-2.5 px-3 text-zinc-600">{j.technician}</td>
                  <td className="py-2.5 px-3 text-right text-emerald-700">${j.revenue.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-right text-red-600">${j.expenses.toLocaleString()}</td>
                  <td className={`py-2.5 px-3 text-right font-bold ${j.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    ${j.profit.toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3">
                    <Badge className={`text-[9px] ${j.status === "closed" ? "bg-zinc-100 text-zinc-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {j.status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {byJob.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-zinc-400">No completed jobs in this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Goals Tab */}
      {activeTab === "goals" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowGoalForm(!showGoalForm)}
              className="bg-red-600 hover:bg-red-700 text-white text-xs"
              data-testid="add-goal-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> {showGoalForm ? "Cancel" : "Set Goal"}
            </Button>
          </div>

          {showGoalForm && (
            <Card className="border border-zinc-200" data-testid="goal-form">
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-zinc-500 font-bold uppercase">Location</label>
                    <select value={goalForm.location_id} onChange={e => setGoalForm(p => ({ ...p, location_id: e.target.value }))}
                      className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
                      data-testid="goal-location-select">
                      <option value="">Select location</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 font-bold uppercase">Period</label>
                    <select value={goalForm.period_type} onChange={e => setGoalForm(p => ({ ...p, period_type: e.target.value }))}
                      className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
                      data-testid="goal-period-select">
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 font-bold uppercase">Start Date</label>
                    <input type="date" value={goalForm.period_start}
                      onChange={e => setGoalForm(p => ({ ...p, period_start: e.target.value }))}
                      className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs"
                      data-testid="goal-start-date" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 font-bold uppercase">Revenue Target ($)</label>
                    <input type="number" value={goalForm.target_revenue}
                      onChange={e => setGoalForm(p => ({ ...p, target_revenue: e.target.value }))}
                      placeholder="10000" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs"
                      data-testid="goal-revenue-target" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 font-bold uppercase">Jobs Target</label>
                    <input type="number" value={goalForm.target_jobs}
                      onChange={e => setGoalForm(p => ({ ...p, target_jobs: e.target.value }))}
                      placeholder="50" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs"
                      data-testid="goal-jobs-target" />
                  </div>
                </div>
                <Button onClick={createGoal} className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs w-full"
                  data-testid="save-goal-btn">Save Goal</Button>
              </CardContent>
            </Card>
          )}

          {goals.length > 0 ? (
            <div className="space-y-3">
              {goals.map(g => (
                <Card key={g.id} className="border border-zinc-200" data-testid={`goal-${g.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-zinc-900">{g.location_name}</p>
                        <p className="text-[10px] text-zinc-400">
                          {g.period_type === "weekly" ? "Weekly" : "Monthly"} starting {new Date(g.period_start).toLocaleDateString()}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => deleteGoal(g.id)}
                        className="text-[10px] h-6 px-2 text-red-500" data-testid={`delete-goal-${g.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-zinc-500 font-bold uppercase">Revenue</span>
                          <span className="text-[10px] font-bold text-zinc-700">
                            ${g.actual_revenue?.toLocaleString()} / ${g.target_revenue?.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${Math.min(g.revenue_progress || 0, 100)}%` }} />
                        </div>
                        <p className="text-[10px] text-zinc-400 mt-0.5">{g.revenue_progress?.toFixed(1)}%</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-zinc-500 font-bold uppercase">Jobs</span>
                          <span className="text-[10px] font-bold text-zinc-700">
                            {g.actual_jobs} / {g.target_jobs}
                          </span>
                        </div>
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${Math.min(g.jobs_progress || 0, 100)}%` }} />
                        </div>
                        <p className="text-[10px] text-zinc-400 mt-0.5">{g.jobs_progress?.toFixed(1)}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed border-zinc-200 rounded-xl">
              <Target className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No goals set yet</p>
              <p className="text-xs text-zinc-400 mt-1">Set weekly or monthly targets for each location</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
